import type * as l from "@cerulea/lexicon";
import type {
  ImagesEmbed,
  PostEmbed,
  RecordEmbed,
  RecordWithMediaEmbed,
  SelfLabels,
  StrongRef,
} from "../structs.ts";

export interface ImageAttachment {
  readonly blob: l.BlobValue;
  readonly alt: string;
  readonly aspectRatio?: { readonly width: number; readonly height: number };
}

/** The image set of a post, whether alone or alongside a quote. */
export function embeddedImages(embed: PostEmbed | undefined): ImagesEmbed["images"] {
  switch (embed?.$type) {
    case "blue.cerulea.app.embed.images":
      return embed.images;
    case "blue.cerulea.app.embed.recordWithMedia":
      return embed.media.$type === "blue.cerulea.app.embed.images" ? embed.media.images : [];
    default:
      return [];
  }
}

export function imagesEmbed(attachments: readonly ImageAttachment[]): ImagesEmbed | undefined {
  if (!attachments.length) return undefined;
  return {
    $type: "blue.cerulea.app.embed.images",
    images: attachments.map((attachment) => ({
      image: attachment.blob,
      ...(attachment.alt && { alt: attachment.alt }),
      ...(attachment.aspectRatio && { aspectRatio: attachment.aspectRatio }),
    })),
  };
}

export function postEmbed(
  attachments: readonly ImageAttachment[],
  quote?: StrongRef,
): PostEmbed | undefined {
  const media = imagesEmbed(attachments);
  const record: RecordEmbed | undefined = quote &&
    { $type: "blue.cerulea.app.embed.record", record: quote };
  if (record && media) {
    const combined: RecordWithMediaEmbed = {
      $type: "blue.cerulea.app.embed.recordWithMedia",
      record,
      media,
    };
    return combined;
  }
  return record ?? media;
}

/** Editing keeps quotes, video, and link cards untouched; only the image set is replaced. */
export function mergeEditEmbed(
  original: PostEmbed | undefined,
  attachments: readonly ImageAttachment[],
): PostEmbed | undefined {
  switch (original?.$type) {
    case undefined:
    case "blue.cerulea.app.embed.images":
      return imagesEmbed(attachments);
    case "blue.cerulea.app.embed.recordWithMedia": {
      const media = imagesEmbed(attachments);
      if (!media) {
        const quote: RecordEmbed = {
          $type: "blue.cerulea.app.embed.record",
          record: original.record.record,
        };
        return quote;
      }
      return { ...original, media };
    }
    default:
      return original;
  }
}

// Bluesky's media label values, so warnings carry across AT Protocol clients.
/** Self-labels that keep a post's images hidden until the reader asks to see them. */
export const mediaLabels = {
  sexual: "Suggestive",
  nudity: "Nudity",
  porn: "Adult content",
  "graphic-media": "Graphic media",
} as const;
export type MediaLabel = keyof typeof mediaLabels;

function isMediaLabel(value: string): value is MediaLabel {
  return Object.hasOwn(mediaLabels, value);
}

/** The media warning a post carries, e.g. "Nudity", if any. */
export function mediaWarning(labels: SelfLabels | undefined): string | undefined {
  const names = (labels?.values ?? []).flatMap(({ val }) =>
    isMediaLabel(val) ? [mediaLabels[val]] : []
  );
  return names.length ? names.join(", ") : undefined;
}

/** The post's media label, if it carries one. */
export function mediaLabel(labels: SelfLabels | undefined): MediaLabel | undefined {
  return labels?.values.map(({ val }) => val).find(isMediaLabel);
}

/** Replace a post's media label, keeping any other self-labels it carries. */
export function withMediaLabel(
  labels: SelfLabels | undefined,
  label: MediaLabel | undefined,
): SelfLabels | undefined {
  const values = [
    ...(labels?.values ?? []).filter(({ val }) => !isMediaLabel(val)),
    ...(label ? [{ val: label }] : []),
  ];
  return values.length ? { $type: "blue.cerulea.app.label#selfLabels", values } : undefined;
}
